import { Formik, Form } from "formik";
import { FormModel } from "../types/FormTypes";
import { useAtom } from "jotai";
import { formDataAtom } from "../App";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useRef } from "react";
import useFocusOnLoad from "../hooks/useFocusOnLoad";

import {
  Box,
  Button,
  handleSubmitClick,
  ErrorList,
  Progressbar,
  RadioGroupForm,
  Heading,
} from "@ogt/ui";

export const WITH_APP = "MobileApp";
export const WITH_TRAVELCARD = "TravelCard";
export const WITH_BLIP = "PaymentCard";
export const WITH_TICKET = "PaperSlip";
export const NO_TICKET = "None";

export default function TicketType() {
  const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
  const [formData, setFormData] = useAtom(formDataAtom);
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const progress = 22;

  return (
    <div>
      <Formik<FormModel>
        onSubmit={(values) => {
          const resetTicketNumber =
            values.ticketType !== formData.ticketType
              ? ""
              : formData.ticketNumber;

          setFormData({
            ...formData,
            ...values,
            ticketNumber: resetTicketNumber,
          });

          if (values.ticketType === WITH_APP) {
            navigate("/biljett-med-app");
          } else if (values.ticketType === WITH_TRAVELCARD) {
            navigate("/biljett-med-resekort");
          } else if (values.ticketType === WITH_BLIP) {
            navigate("/biljett-med-blipp");
          } else if (values.ticketType === WITH_TICKET) {
            navigate("/pappersbiljett");
          } else if (values.ticketType === NO_TICKET) {
            navigate("/vad-var-det-som-hande");
          }
        }}
        initialValues={{
          ...formData,
        }}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={Yup.object().shape({
          ticketType: Yup.string().test(
            "at-least-one-radio-selected",
            "Frågan “Var fanns biljetten?” saknar svar",
            (value) => {
              return (
                value === WITH_APP ||
                value === WITH_TRAVELCARD ||
                value === WITH_BLIP ||
                value === WITH_TICKET ||
                value === WITH_TRAVELCARD ||
                value === NO_TICKET
              );
            }
          ),
        })}
      >
        {({ errors, values }) => (
          <>
            <div className="flex justify-end items-center">
              <div className="w-full max-w-xs mb-4">
                <Progressbar percentage={progress} />
              </div>
            </div>
            <Form noValidate>
              <Box>
                <Heading
                  progress={progress}
                  headingRef={h2Ref}
                  focusable
                  id="ticketDetailsHeading"
                >
                  Var fanns biljetten?
                </Heading>

                <p>
                  För att kunna hjälpa dig behöver vi veta var biljetten fanns.
                  Välj ett av alternativen för att gå vidare. Om det inte fanns
                  någon biljett, välj ”Inget av ovanstående/det fanns ingen
                  biljett”.
                </p>

                {Object.keys(errors).length > 0 && (
                  <ErrorList errors={errors} />
                )}

                <RadioGroupForm
                  name="ticketType"
                  ariaLabelledBy="ticketDetailsHeading"
                  options={[
                    { id: "MobileApp", value: "MobileApp", label: "I appen" },
                    {
                      id: "TravelCard",
                      value: "TravelCard",
                      label: "På ett reskort",
                    },
                    {
                      id: "PaymentCard",
                      value: "PaymentCard",
                      label: "Blippat betalkort ombord",
                    },
                    {
                      id: "PaperSlip",
                      value: "PaperSlip",
                      label: "Pappersbiljett",
                    },
                    {
                      id: "None",
                      value: "None",
                      label: "Inget av ovanstående/det fanns ingen biljett",
                    },
                  ]}
                />
              </Box>

              <div className="flex justify-center">
                <div className="w-full text-left flex items-center">
                  <Button
                    iconPosition="left"
                    iconSize={16}
                    iconName="arrow-left"
                    aria-label="Tillbaka"
                    showIcon
                    size="medium"
                    variant="outline"
                    onClick={(event) => {
                      event.preventDefault();
                      const backUrl = undefined;
                      if (backUrl) {
                        navigate(backUrl, { replace: true });
                      } else {
                        navigate(-1);
                      }
                    }}
                  >
                    Tillbaka
                  </Button>
                </div>
                <div className="w-full text-right">
                  <Button onClick={handleSubmitClick(formRef)} type="submit">
                    Gå vidare
                  </Button>
                </div>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
}
